import { CommonModule } from '@angular/common';
import {
    Component,
    ElementRef,
    afterNextRender,
    booleanAttribute,
    computed,
    inject,
    input,
    model,
    viewChild,
    viewChildren,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Color, Rounded, Size, Variant } from '../common/types';
import { SegmentOptionComponent } from './option/segment-option.component';

export type SegmentOption = {
    label: string;
    value: string;
    icon?: string;
    count?: number;
    height?: Size;
    renderItem?: any;
    badge?: {
        label: string;
        color: Color;
        size: Size;
        rounded: Rounded;
        variant: Variant;
    };
};

@Component({
    selector: 'app-segment',
    templateUrl: './segment.component.html',
    styleUrls: ['./segment.component.css'],
    standalone: true,
    imports: [CommonModule, SegmentOptionComponent],
})
export class Segment {
    private readonly elementRef = inject(ElementRef);

    container = viewChild<ElementRef<HTMLElement>>('container');
    indicator = viewChild<ElementRef<HTMLElement>>('indicator');
    items = viewChildren<SegmentOptionComponent>('item');

    color = input<Color>('primary');
    $options = input<string[] | SegmentOption[]>([], { alias: 'options' });

    options = computed<SegmentOption[]>(() => {
        if (this.$options().length === 0) return [];

        return this.$options().map((option) => {
            if (typeof option === 'string') {
                return {
                    label: option,
                    value: option,
                };
            }

            return option;
        });
    });

    value = model<string>(this.options()[0]?.value);
    value$ = toObservable(this.value);

    scrollEnabled = input<boolean, string>(false, {
        transform: booleanAttribute,
    });

    constructor() {
        afterNextRender(() => {
            if (!this.value()) this.value.set(this.options()[0].value);

            this.value$.subscribe({
                next: (value) => {
                    if (this.container() && value && this.scrollEnabled()) {
                        setTimeout(() => {
                            const left = this.getTranslateX(this.options().findIndex((item) => item.value === value));
                            this.container()!.nativeElement.scrollBy({
                                left,
                            });
                        }, 100);
                    }
                },
            });

            setTimeout(() => {
                this.value$.subscribe((value) => {
                    if (this.items() && this.indicator() && value) {
                        this.transform();
                    }
                });
            }, 500);
        });
    }

    handleClick(option: SegmentOption) {
        this.value.set(option.value);
    }

    transform() {
        const index = this.options().findIndex((item) => item.value === this.value());
        if (index === -1) return;
        this.indicator()!.nativeElement.style.width = `${this.items()[index].elementRef.nativeElement!.offsetWidth}px`;
        const translateX = this.getTranslateX(index);
        this.indicator()!.nativeElement.style.transform = `translateX(${translateX}px)`;
    }

    getTranslateX(index: number) {
        return this.items()
            .slice(0, index)
            .reduce((acc, cur) => acc + cur.elementRef.nativeElement.offsetWidth, 0);
    }
}
